import { Fragment, ReactNode } from 'react'
import { isUndefined } from 'lodash'
import { LabelList, Line } from 'recharts'

import {
  ADDITION_SUFFIX,
  ADDITIONAL_DATA_KEY,
  ADDITIONS_OPACITY,
  TEXT_SIZE,
} from 'consts/chart'
import { ChartData, ChartItemWitSelection } from 'types'
import { makeSafeDataKeyExtractor } from 'utils/charts/extractSafeDataKey'

import { LineChartDot } from './LineChartDot'
import { LineChartLabel } from './LineChartLabel'

type AdditionalLineProps = {
  chart: ChartItemWitSelection
  entityName: string
  activeLineName?: string
  additionActiveLineName?: string
  additionLabel?: string
  additionLabelPosition?: { dy: number }
  tooltipPayload?: ChartData[0]
  showLabel?: boolean
  onHighlight: (name?: string) => void
}

export const renderAdditionalLine = ({
  chart,
  activeLineName,
  additionActiveLineName,
  additionLabel,
  additionLabelPosition,
  entityName,
  tooltipPayload,
  showLabel,
  onHighlight,
}: AdditionalLineProps): ReactNode => {
  const { name, value, color, selected } = chart
  const additionalValue = `${value}${ADDITION_SUFFIX}`

  const hasActiveLine = !isUndefined(activeLineName)
  const hasAdditionalActiveLine = !isUndefined(additionActiveLineName)
  const isHighlighted =
    (hasAdditionalActiveLine && additionActiveLineName === name) ||
    (hasActiveLine && activeLineName === name)
  const hideDots =
    (hasAdditionalActiveLine && additionActiveLineName !== name) ||
    (hasActiveLine && activeLineName !== name)

  const handleChartEnter = () => {
    onHighlight(name)
  }
  const handleChartLeave = () => {
    onHighlight(undefined)
  }
  return (
    <Fragment key={additionalValue}>
      <Line
        name={additionalValue}
        dataKey={makeSafeDataKeyExtractor(ADDITIONAL_DATA_KEY, value)}
        stroke={color}
        strokeWidth={1.5}
        strokeDasharray="5 2"
        strokeOpacity={ADDITIONS_OPACITY}
        type="monotone"
        pointerEvents={hideDots ? 'none' : undefined}
        hide={!selected}
        activeDot={false}
        dot={({ key, ...props }) =>
          hideDots ? (
            // recharts Line.dot requires Element to be returned
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          ) : (
            <LineChartDot
              key={key}
              {...props}
              dataKeyProperty={value}
              entityName={entityName}
              additionLabel={additionLabel}
              strokeOpacity={ADDITIONS_OPACITY}
              isShown={isHighlighted}
              isTooltipOpen={Boolean(tooltipPayload) && props.payload === tooltipPayload}
              isAddition
            />
          )
        }
        label={props =>
          isHighlighted ? (
            <LineChartLabel {...props} color={color} />
          ) : (
            // recharts Line.label requires Element to be returned
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          )
        }
        isAnimationActive={false}
        onMouseEnter={handleChartEnter}
        onMouseLeave={handleChartLeave}
      >
        {(showLabel || isHighlighted) && (
          <LabelList
            content={label => {
              return label.index === 0 ? (
                <text
                  x={label.x}
                  y={label.y}
                  textAnchor="start"
                  fontSize={TEXT_SIZE}
                  fill={color}
                  fillOpacity={ADDITIONS_OPACITY}
                  {...additionLabelPosition}
                >
                  {additionLabel}
                </text>
              ) : null
            }}
          />
        )}
      </Line>
    </Fragment>
  )
}
